import React, { ReactNode, useContext, useMemo } from "react";
import { RulesEditorProviderValue, RulesEditorContext } from ".";
import { MCard, MSelect } from "components/Shared";
import { KeyValueOriginal, MCardField } from "components/Shared/MCard";
import { setObjectValue } from "components/Shared/Functions";
import { DATA_TYPE_DROPDOWN2 } from "typeConstants/dataTypes";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import IconButton from "components/Shared/IconButton";
import { createSelectGroupEnumItemId, isQuestionSelectGroup } from "../Designer/helpers";
import { findQuestionDef } from "../Designer/DesignerQuestionDefinitions";
import FormEnumerationEditor, { EnumerationEditorField } from "../Designer/FormEnumerationEditor";
import { EnumerationItem, RuleCategory } from "interfaces/Phoenix.FormsDesigner.Models";

const RulesEditorProps = () => {
  const context = useContext<RulesEditorProviderValue>(RulesEditorContext);
  const { selectedRule, settings } = context;
  const control = selectedRule?.control;

  const questionScopes = useMemo(() => {
    if (context.linkedForm) {
      return [
        { label: "Form Scope", value: "" },
        ...context.linkedForm.questionLibrary.questions.filter(q => q.control.type === "Repeat").map(q => ({ label: q.control.label, value: q.control.dataField.xpath })),
      ];
    } else {
      return [];
    }
  }, [context.linkedForm]);

  let cards = [<div>Select a control...</div>];

  if (control) {
    const onPropertyEdit = (changeArray: KeyValueOriginal[]) => {
      changeArray.forEach(ca => {
        setObjectValue(control, ca.key, ca.value);
      });

      context.refreshRulesetDef("control field edited");
    };

    var controlTypeText = findQuestionDef(selectedRule)?.name;

    // Properties card: simplified for rules (Label + Scope)
    let propertiesCardFields: MCardField[] = [
      { label: "Question Type", value: controlTypeText, editable: false },
      { key: "label", label: "Label" },
      {
        key: "dataField.xpath",
        label: "Scope",
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: questionScopes, nullable: false },
        value: questionScopes.find(o => o.value === control.dataField.xpath),
      },
    ];

    propertiesCardFields.forEach(f => {
      if (f.value === undefined && f.key) f.value = f.key.split(".").reduce((acc, val) => acc ? acc[val] : undefined, control);
    });

    cards = [
      <MCard
        key={`props-${context.selectedRule.id}`}
        onlyEditable
        onEditChanged={onPropertyEdit}
        fields={propertiesCardFields}
        title="Properties"
        isFetching={context.isLoadingLinkedForm}
      />,
    ];

    const enumeration = control.dataField.enumeration;
    const ruleSettings = selectedRule.control.type === "Ruleset" ? settings?.ruleSettings : settings?.recommendationSettings;

    if (control.dataField?.enumeration) {

      // Categories section
      const currentCategories = enumeration.name ? enumeration.name.split("|") : [];

      let selectItems: RuleCategory[] = ruleSettings?.categories ?? [];

      for (let i = 0; i < currentCategories.length; i++) {
        const c = currentCategories[i];
        const filtered = selectItems.filter(x => c === x.name);

        if (filtered.length && filtered[0].categories?.length) {
          selectItems = filtered[0].categories;
        } else {
          selectItems = [];
          break;
        }
      }

      const categoryOptions = selectItems.map(x => x.name).sort().map(c => ({ label: c, value: c }));

      const addCategory = (name: string) => {
        enumeration.name = enumeration.name ? enumeration.name + "|" + name : name;
        context.refreshRulesetDef("control field edited");
      };

      const removeCategory = (index: number) => {
        const cats = enumeration.name.split("|");
        cats.splice(index, cats.length - index);
        enumeration.name = cats.join("|");
        context.refreshRulesetDef("control field edited");
      };

      let categories = [] as string[];
      if (enumeration?.name?.length) categories = enumeration?.name?.split("|");

      let categoryCard = categories.map((c, index) => <div className="edit-category">{c}<IconButton icon="edit" onClick={() => removeCategory(index)} /></div>);

      if (categoryOptions.length) categoryCard.push(<MSelect label="Add Category" options={categoryOptions} onChange={c => addCategory(c.value)} />);

      cards.push(
        <MCard
          key={`categories-${context.selectedRule.id}-${context.rulesetDefinitionUpdatedAt}`}
          title="Categories"
          children={categoryCard}
          onlyEditable
          onEditChanged={(changeArray: KeyValueOriginal[]) => {
            const catChange = changeArray.find(c => c.key === "addCategory");
            if (catChange && catChange.value?.value) {
              addCategory(catChange.value.value);
            }
          }}
        />
      );
    }

    // Handle Recommendation type
    if (control.type === "Ruleset") {
      const ruleLevels = ruleSettings?.levels ?? [];

      // Rules items section
      cards.push(
        <FormEnumerationEditor
          title="Rules"
          items={enumeration.items ?? []}
          fields={[
            { label: "Level", property: "value", required: true, options: ruleLevels.map(l => ({ label: l.name, value: l.name })) },
            { label: "Expression", property: "selectedExpression", isExpression: true },
            { label: "Hazard", property: "relevantExpression", isExpression: true },
            { label: "Description", property: "name", isExpression: true },
          ] as EnumerationEditorField<EnumerationItem>[]}
          onChange={newItems => {
            // Ensure new items have default level
            newItems.forEach(item => {
              if (!item.value && ruleLevels.length > 0) {
                item.value = ruleLevels[0].name;
              }
            });
            context.editFns.setQuestionListItems(selectedRule.id, newItems);
          }}
          displayItem={item => {
            let parts: ReactNode[] = [];
            if (item.value) parts.push(`[${item.value}]`);
            if (item.relevantExpression) parts.push(` Hazard: ${item.relevantExpression}`);
            if (item.name) parts.push(` - ${item.name}`);
            if (item.selectedExpression) parts.push(<br />, `Expression: ${item.selectedExpression}`);
            return parts.length > 0 ? parts : "Empty Rule";
          }}
        />
      );
    }

    // Handle Recommendation type
    if (control.type === "Recommendation") {
      const enumeration = control.dataField.enumeration;
      const ruleLevels = ruleSettings?.levels ?? [];
      
      cards.push(
        <FormEnumerationEditor
          title="Recommendations"
          stackEditDelete
          items={enumeration.items ?? []}
          fields={[
            { label: "Level", property: "value", required: true, options: ruleLevels.map(l => ({ label: l.name, value: l.name })) },
            { label: "Text Color", property: "color" },
            { label: "BG Color", property: "backgroundColor" },
            { label: "Expression", property: "relevantExpression", isExpression: true },
            { label: "Recommendation", property: "recommendationExpression", isExpression: true },
            { label: "Location", property: "selectedExpression", isExpression: true },
            { label: "Linked Photo", property: "linkedPhoto" },
            { label: "Linked Question", property: "linkedQuestion" },
          ] as EnumerationEditorField<EnumerationItem>[]}
          onChange={newItems => context.editFns.setQuestionListItems(selectedRule.id, newItems)}
          displayItem={item => {
            return (
              <MCard
                dontRenderTitle
                fields={[
                  { label: "Level", value: item.value },
                  { label: "Text Color", value: item.color },
                  { label: "BG Color", value: item.backgroundColor },
                  { label: "Expression", value: item.relevantExpression },
                  { label: "Recommendation", value: item.recommendationExpression },
                  { label: "Location", value: item.selectedExpression },
                  { label: "Linked Photo", value: item.linkedPhoto },
                  { label: "Linked Question", value: item.linkedQuestion },
                ]}
              />);
          }}
        />
      );
    }

    // Standard enumeration for SelectOne/SelectMany/SelectGroup
    let showEnumeration = !!selectedRule?.control?.dataField?.enumeration;
    if (control.type === "Ruleset" || control.type === "Recommendation" || control.type === "Photo" ||
        control.type.startsWith("DynamicItemset") || control.type.startsWith("AdvancedDynamicItemset")) {
      showEnumeration = false;
    }

    let isSelectGroup = isQuestionSelectGroup(selectedRule);

    if (showEnumeration) {
      const enumerationItems = control.dataField.enumeration?.items;
      let fields: EnumerationEditorField<EnumerationItem>[] = [];
      let bulkEditItems: (keyof EnumerationItem)[] = [];

      if (isSelectGroup) {
        bulkEditItems = ["name", "value", "xpath", "bindableId", "exportId", "relevantExpression", "selectedExpression"];
        fields = [
          { label: "Text", property: "name", required: true, blockCommas: true },
          { label: "Value (if different from text)", property: "value", blockCommas: true },
          { label: "XPath", property: "xpath" },
          { label: "Export ID", property: "exportId" },
          { label: "Bindable ID", property: "bindableId", hideOnAdd: true },
          { label: "Conditional Display", property: "relevantExpression", isExpression: true },
          { label: "Selected Condition", property: "selectedExpression", isExpression: true },
        ];
      } else {
        bulkEditItems = ["name", "value"];
        fields = [
          { label: "Text", property: "name", required: true, blockCommas: true },
          { label: "Value (if different from text)", property: "value", blockCommas: true },
        ];
      }

      const defaultEnumFields = (item: EnumerationItem) => {
        if (isSelectGroup) {
          item.bindableId = createSelectGroupEnumItemId(selectedRule.control.dataField.bindableId, item.name);
        }
        if (!item.value) item.value = item.name;
      };

      cards.push(<FormEnumerationEditor
        title="List Items"
        items={enumerationItems}
        fields={fields}
        bulkEditFields={bulkEditItems}
        onChange={newItems => context.editFns.setQuestionListItems(selectedRule.id, newItems)}
        displayItem={item => {
          let display: ReactNode[] = [item.name];
          if (item.name !== item.value) display[0] = `${display[0]} (${item.value})`;
          if (item.bindableId) display.push(<br />, `#${item.bindableId}`);
          if (item.xpath) display.push(<br />, `XPATH: ${item.xpath}`);
          if (item.exportId) display.push(<br />, `EXPORT-ID: ${item.exportId}`);
          if (item.relevantExpression) display.push(<br />, `CONDITIONAL: ${item.relevantExpression}`);
          if (item.selectedExpression) display.push(<br />, `SELECTED: ${item.selectedExpression}`);
          return display;
        }}
        preAddLogic={defaultEnumFields}
      />);
    }
  }

  return (
    <div className="designer-props">
      {cards}
    </div>
  );
};

export default RulesEditorProps;
